// Modules

import { getColorModeFromStorage, getFocusModeFromStorage } from '@src/modules/local_storage_utils';
import store from '@src/store/index';
import { updateBizSkillsFromStorage } from '@src/store/slices/biz_skills';
import { updateCareerSummary } from '@src/store/slices/career_summary';
import { updateContact } from '@src/store/slices/contact/resume';
import { updateRirekishoContact } from '@src/store/slices/contact/rirekisho';
import { updateEducationalBackground } from '@src/store/slices/educational_background';
import { updateColorMode, updateFocusMode } from '@src/store/slices/focus_mode';
import { updateHistoriesFromStorage } from '@src/store/slices/history/resume';
import { updateRirekishoHistory } from '@src/store/slices/history/rirekisho';
import { updateNextCareer } from '@src/store/slices/next_career';
import { updatePrivateActivityFromStorage } from '@src/store/slices/private_activity';
import { updateProjectsFromStorage } from '@src/store/slices/project';
import { updateRirekishoAddress } from '@src/store/slices/rirekisho/address';
import { updateRirekishoBasicInfo } from '@src/store/slices/rirekisho/basic_info';
import { updateRirekishoLicense } from '@src/store/slices/rirekisho/license';
import { updateRirekishoMotivation } from '@src/store/slices/rirekisho/motivation';
import { updateRirekishoOptions } from '@src/store/slices/rirekisho/options';
import { updateRirekishoPersonalRequests } from '@src/store/slices/rirekisho/personal_requests';
import { updateRirekishoPhoto } from '@src/store/slices/rirekisho/photo';
import { updateSkillsFromStorage } from '@src/store/slices/skill';
import { updateSkillComment } from '@src/store/slices/skill_comment';
// 統合スライス
import { updateBlind, updateFirstView } from '@src/store/slices/ui';
import {
  updateResumeInputStatus,
  updateRirekishoInputStatus,
} from '@src/store/slices/unified/input_status';
import { batch } from 'react-redux';

export default function initializeState(s: any) {
  batch(() => {
    // 新しいRirekisho構造からの復元
    if (s.rirekisho) {
      if (s.rirekisho.basicInfo) {
        store.dispatch(updateRirekishoBasicInfo(s.rirekisho.basicInfo));
      }
      if (s.rirekisho.currentAddress) {
        store.dispatch(updateRirekishoAddress(s.rirekisho.currentAddress));
      }
      if (s.rirekisho.contact) {
        store.dispatch(updateRirekishoContact(s.rirekisho.contact));
      }
      if (s.rirekisho.history) {
        store.dispatch(updateRirekishoHistory(s.rirekisho.history));
      }
      if (s.rirekisho.licenses) {
        store.dispatch(updateRirekishoLicense(s.rirekisho.licenses));
      }
      if (s.rirekisho.motivation) {
        store.dispatch(updateRirekishoMotivation(s.rirekisho.motivation));
      }
      if (s.rirekisho.personalRequests) {
        store.dispatch(updateRirekishoPersonalRequests(s.rirekisho.personalRequests));
      }
      if (s.rirekisho.options) {
        store.dispatch(updateRirekishoOptions(s.rirekisho.options));
      }
      if (s.rirekisho.photo) {
        store.dispatch(updateRirekishoPhoto(s.rirekisho.photo));
      }
    }

    // 分離されたスライスからの復元
    if (s.resumeContact) {
      store.dispatch(updateContact(s.resumeContact));
    }
    if (s.rirekishoContact) {
      store.dispatch(updateRirekishoContact(s.rirekishoContact));
    }

    // 後方互換性: 統合スライスからの移行
    const oldState = s as any;
    if (oldState.unifiedContact) {
      store.dispatch(updateContact(oldState.unifiedContact.resume));
      store.dispatch(updateRirekishoContact(oldState.unifiedContact.rirekisho));
    } else {
      // 後方互換性: 旧スライスからの移行
      if (oldState.contact) {
        store.dispatch(updateContact(oldState.contact));
      }
      if (oldState.rirekishoContact) {
        store.dispatch(updateRirekishoContact(oldState.rirekishoContact));
      }
    }

    store.dispatch(updateCareerSummary(s.careerSummary.content));
    store.dispatch(updateSkillComment(s.skillComment.content));
    store.dispatch(updateNextCareer(s.nextCareer.content));
    // EntityAdapter migration - use empty array if old structure
    const privateActivities = Array.isArray(s.privateActivity)
      ? s.privateActivity
      : (s.privateActivity as any).activities || [];
    store.dispatch(updatePrivateActivityFromStorage(privateActivities));
    store.dispatch(updateEducationalBackground(s.educationalBackground));

    // UI状態の復元（統合スライス優先、後方互換性も保持）
    if (s.ui) {
      store.dispatch(updateFirstView(s.ui.firstView));
      store.dispatch(updateBlind(s.ui.blind));
    } else {
      // 後方互換性: 旧スライスから
      const oldState = s as any;
      if (oldState.firstview) {
        store.dispatch(updateFirstView(oldState.firstview.firstView));
      }
      if (oldState.blind) {
        store.dispatch(updateBlind(oldState.blind.blind));
      }
    }

    // History状態の復元（分離されたスライス優先）
    if (s.resumeHistory) {
      store.dispatch(updateHistoriesFromStorage(s.resumeHistory.histories));
    }
    if (s.rirekishoHistory) {
      store.dispatch(updateRirekishoHistory(s.rirekishoHistory));
    }

    // 後方互換性: 統合スライスからの移行
    if (oldState.unifiedHistory) {
      store.dispatch(updateHistoriesFromStorage(oldState.unifiedHistory.resume.histories));
      store.dispatch(updateRirekishoHistory(oldState.unifiedHistory.rirekisho));
    } else {
      // 後方互換性
      if (oldState.history) {
        store.dispatch(updateHistoriesFromStorage(oldState.history.histories));
      }
      if (oldState.rirekishoHistory) {
        store.dispatch(updateRirekishoHistory(oldState.rirekishoHistory));
      }
    }

    // EntityAdapter migration - use empty array if old structure
    const skills = Array.isArray(s.skill) ? s.skill : (s.skill as any).skills || [];
    store.dispatch(updateSkillsFromStorage(skills));
    // EntityAdapter migration - use empty array if old structure
    const bizSkills = Array.isArray(s.bizSkill) ? s.bizSkill : (s.bizSkill as any).bizSkills || [];
    store.dispatch(updateBizSkillsFromStorage(bizSkills));
    store.dispatch(updateProjectsFromStorage(s.project.projects));

    // InputStatus状態の復元（統合スライス優先）
    if (s.unifiedInputStatus) {
      store.dispatch(updateResumeInputStatus(s.unifiedInputStatus.resume));
      store.dispatch(updateRirekishoInputStatus(s.unifiedInputStatus.rirekisho));
    } else {
      // 後方互換性
      const oldState = s as any;
      if (oldState.inputStatus) {
        store.dispatch(updateResumeInputStatus(oldState.inputStatus));
      }
      if (oldState.rirekishoInputStatus) {
        store.dispatch(updateRirekishoInputStatus(oldState.rirekishoInputStatus));
      }
    }

    // フォーカスモードとカラーモードの状態を復元
    // Redux状態を優先し、足りない場合はローカルストレージから復元
    const persistedFocusMode = s.focusMode?.isFocusMode;
    const persistedColorMode = s.focusMode?.colorMode;
    const storedFocusMode = getFocusModeFromStorage();
    const storedColorMode = getColorModeFromStorage();

    if (typeof persistedFocusMode === 'boolean') {
      store.dispatch(updateFocusMode(persistedFocusMode));
    } else {
      store.dispatch(updateFocusMode(storedFocusMode));
    }

    if (
      persistedColorMode === 'light' ||
      persistedColorMode === 'dark' ||
      persistedColorMode === 'system'
    ) {
      store.dispatch(updateColorMode(persistedColorMode));
    } else if (storedColorMode) {
      store.dispatch(updateColorMode(storedColorMode));
    } else {
      store.dispatch(updateColorMode('system'));
    }

    // 履歴書フォームの初期値設定
    if (s.rirekishoBasicInfo) {
      store.dispatch(updateRirekishoBasicInfo(s.rirekishoBasicInfo));
    }
    if (s.rirekishoPhoto) {
      store.dispatch(updateRirekishoPhoto(s.rirekishoPhoto));
    }
    if (s.rirekishoAddress) {
      store.dispatch(updateRirekishoAddress(s.rirekishoAddress));
    }
    if (s.rirekishoLicense) {
      store.dispatch(updateRirekishoLicense(s.rirekishoLicense));
    }
    if (s.rirekishoMotivation) {
      store.dispatch(updateRirekishoMotivation(s.rirekishoMotivation));
    }
    if (s.rirekishoPersonalRequests) {
      store.dispatch(updateRirekishoPersonalRequests(s.rirekishoPersonalRequests));
    }
    if (s.rirekishoOptions) {
      store.dispatch(updateRirekishoOptions(s.rirekishoOptions));
    }
  });
}
